import {
	CardBody,
	CardFooter,
	CheckboxField,
	DateTimeField,
	DeleteAction,
	RadioGroupField,
	SubmitButton,
	TextField,
	TextareaField,
	useDynamicConfig,
} from "@dgs/core";
import { Form, Formik } from "formik";
import { FC } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { AppNotificationActionTypeSelect } from "../appNotifications/AppNotificationActionTypeSelect";
import { useAppNotification } from "../appNotifications/useAppNotification";
import { BottomNavBarItemSingleSelect } from "../shared/select/BottomNavBarItemSingleSelect";
import { EventSingleSelect } from "../shared/select/EventSelect";
import { EventWithPMSingleSelect } from "../shared/select/EventWithPMSingleSelect";
import { AppNotificationActionType, AppNotificationFormValues } from "../shared/types/appNotification";
import { RecipientType } from "../shared/types/recipient";
import { UserWithDevicesMultiSelectField } from "../users/UserSelectField";

const StyledCheckboxField = styled(CheckboxField)`
	margin-bottom: ${({ theme }) => theme.spacing(4)};
`;

interface Props {
	appNotification: AppNotificationFormValues;
	handleSubmit: (values: AppNotificationFormValues) => Promise<void>;
	disabled: boolean;
	handleDelete?: () => void;
}

export const AppNotificationForm: FC<Props> = ({ appNotification, handleSubmit, disabled, handleDelete }) => {
	const { t } = useTranslation();
	const { hasPublicAppFeatures } = useDynamicConfig();
	const { validate } = useAppNotification();

	return (
		<Formik initialValues={appNotification} onSubmit={handleSubmit} validate={validate}>
			{(formik) => (
				<Form>
					<CardBody>
						<TextField name="title" label={t("Title")} disabled={disabled} required />
						<TextareaField name="body" label={t("Body")} disabled={disabled} required />
						<StyledCheckboxField
							name="showSendAt"
							label={t("Specific date")}
							onChange={() => formik.setFieldValue("sendAt", null)}
							disabled={disabled}
						/>
						{formik.values.showSendAt && <DateTimeField name="sendAt" label={t("Send at")} disabled={disabled} />}
						<RadioGroupField
							options={[
								...(hasPublicAppFeatures
									? [
											{
												name: t("Send to all devices"),
												value: RecipientType.ALL_DEVICES,
											},
											{
												name: t("Send to devices without user"),
												value: RecipientType.ALL_DEVICES_WITHOUT_USERS,
											},
										]
									: []),
								{
									name: t("Send to devices with user"),
									value: RecipientType.ALL_DEVICES_WITH_USERS,
								},
								{
									name: t("Select recipients"),
									value: RecipientType.SELECTED_USERS,
								},
								{
									name: t("Select event"),
									value: RecipientType.PARTICIPANT_MANAGEMENT_CONTACTS,
								},
							]}
							label={t("Recipients")}
							name="recipientType"
							disabled={disabled}
						/>
						{formik.values.recipientType === RecipientType.SELECTED_USERS && (
							<UserWithDevicesMultiSelectField
								name="selectedRecipients"
								label={t("Select recipients")}
								disabled={disabled}
							/>
						)}
						{formik.values.recipientType === RecipientType.PARTICIPANT_MANAGEMENT_CONTACTS && (
							<EventWithPMSingleSelect name="event" label={t("Event")} disabled={disabled} />
						)}
						<AppNotificationActionTypeSelect
							disabled={disabled}
							onChange={() => formik.setFieldValue("actionValue", null)}
						/>
						{formik.values.actionType === AppNotificationActionType.OPEN_TAB && (
							<BottomNavBarItemSingleSelect
								name="actionValue"
								label={t("App tab to open")}
								required
								disabled={disabled}
							/>
						)}
						{formik.values.actionType === AppNotificationActionType.OPEN_EVENT && (
							<EventSingleSelect name="actionValue" label={t("Event to open")} required disabled={disabled} />
						)}
						{formik.values.actionType === AppNotificationActionType.OPEN_WEBSITE && (
							<TextField name="actionValue" label={t("Url of the website to open")} required disabled={disabled} />
						)}
					</CardBody>
					<CardFooter>
						{!disabled && handleDelete && (
							<DeleteAction
								title={t("Delete")}
								action={handleDelete}
								heading={t("Delete notification")}
								buttonLabel={t("Delete")}
								labels={{ close: t("Close"), confirm: t("Confirm") }}
							>
								{t("Are you sure you want to delete this notification?")}
							</DeleteAction>
						)}
						<SubmitButton disabled={disabled}>{formik.values.showSendAt ? t("Schedule send") : t("Send")}</SubmitButton>
					</CardFooter>
				</Form>
			)}
		</Formik>
	);
};
